// QuizManagement.js

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { PlusCircle, Edit2, X, ChevronDown, ChevronUp, Upload } from 'lucide-react';
import { QuizBulkUpload } from '../forms';
import { ConfirmDialog } from '../modals';
import { SUBJECTS } from '../../constants';
import { getSubjectName } from '../../utils/subjectUtils';

// 分離したコンポーネントのインポート
import ContentEditModal from '../quiz/management/ContentEditModal';
import QuizEditModal from '../quiz/management/QuizEditModal';

// サービスとフックのインポート
import { useContentManagement } from '../../hooks/useContentManagement';
import { quizService } from '../../services/quizService';

const ALL_SUBJECTS = [
  { id: 'all', name: '全教科' },
  ...SUBJECTS
];

const QuizManagement = ({ currentUser }) => {
  // カスタムフックを使用
  const {
    filteredContents,
    activeSubject,
    newContent,
    formErrors,
    editingContentId,
    isMaster,
    setActiveSubject,
    setNewContent,
    setFormErrors,
    handleAddContent,
    handleUpdateContent,
    handleDeleteContent,
    startEditContent,
    resetForm
  } = useContentManagement(currentUser);

  // ローカル状態
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [addingQuiz, setAddingQuiz] = useState(null);
  const [expandedContents, setExpandedContents] = useState({});
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [contentModalOpen, setContentModalOpen] = useState(false);
  const [quizModalOpen, setQuizModalOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);

  // クイズ追加処理
  const handleAddQuiz = (contentId, subject) => {
    if (!isMaster) {
      alert('クイズの追加権限がありません。');
      return;
    }
    
    const initialQuizData = quizService.createQuizData({ 
      type: 'multiple-choice',
      subject: subject
    });
    
    setAddingQuiz({
      contentId,
      quiz: initialQuizData
    });
    setQuizModalOpen(true);
  };

  // クイズ編集処理
  const handleEditQuiz = (contentId, quiz) => {
    if (!isMaster) {
      alert('クイズの編集権限がありません。');
      return;
    }
    
    setEditingQuiz({
      contentId,
      quiz
    });
    setQuizModalOpen(true);
  };

  // クイズ送信処理
  const handleSubmitQuiz = async (quizData) => {
    if (!isMaster) {
      alert('クイズの操作権限がありません。');
      return;
    }

    const errors = quizService.validateQuizData(quizData);
    setFormErrors(errors);
    
    if (Object.keys(errors).length > 0) return;

    try {
      if (editingQuiz) {
        // 更新
        await quizService.updateQuiz(editingQuiz.contentId, editingQuiz.quiz.id, quizData);
      } else if (addingQuiz) {
        // 追加
        await quizService.addQuiz(addingQuiz.contentId, quizData);
      }
      
      // 状態をリセット
      setEditingQuiz(null);
      setAddingQuiz(null);
      setFormErrors({});
      setQuizModalOpen(false);
    } catch (error) {
      console.error('クイズの保存に失敗しました:', error);
      alert('クイズの保存に失敗しました。再試行してください。');
    }
  };

  // クイズ削除処理
  const handleDeleteQuiz = async (contentId, quizId) => {
    if (!isMaster) {
      alert('クイズの削除権限がありません。');
      return;
    }

    setConfirmDialog({
      title: 'クイズの削除',
      message: 'このクイズを削除してもよろしいですか？',
      onConfirm: async () => {
        try {
          await quizService.deleteQuiz(contentId, quizId);
        } catch (error) {
          console.error('クイズの削除に失敗しました:', error);
          alert('クイズの削除に失敗しました。再試行してください。');
        }
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  // コンテンツ削除の確認付きハンドラ
  const handleDeleteContentWithConfirm = (contentId) => {
    setConfirmDialog({
      title: 'コンテンツの削除',
      message: 'このコンテンツとすべてのクイズを削除してもよろしいですか？',
      onConfirm: async () => {
        try {
          await handleDeleteContent(contentId);
        } catch (error) {
          alert('コンテンツの削除に失敗しました。再試行してください。');
        }
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  // コンテンツ追加のハンドラ
  const handleAddContentWithModal = async () => {
    try {
      const success = await handleAddContent();
      if (success) {
        setContentModalOpen(false);
      }
    } catch (error) {
      alert('コンテンツの追加に失敗しました。再試行してください。');
    }
  };

  // コンテンツ更新のハンドラ
  const handleUpdateContentWithModal = async () => {
    try {
      const success = await handleUpdateContent();
      if (success) {
        setContentModalOpen(false);
      }
    } catch (error) {
      alert('コンテンツの更新に失敗しました。再試行してください。');
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold text-purple-700 mb-6">クイズ管理</h2>
      
      {/* マスター教師用の一括アップロードボタン */}
      {isMaster && (
        <div className="mb-6">
          <button
            onClick={() => setShowBulkUpload(true)}
            className="w-full py-4 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-500 hover:text-purple-500 flex items-center justify-center gap-2 transition-colors duration-200"
          >
            <Upload size={20} />
            エクセルファイルから一括登録
          </button>
        </div>
      )}

      {/* 一括アップロードモーダル */}
      {showBulkUpload && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="absolute inset-0 bg-black opacity-50" />
          <div className="relative bg-white rounded-lg shadow-lg max-w-2xl w-full m-4">
            <QuizBulkUpload onClose={() => setShowBulkUpload(false)} />
          </div>
        </div>
      )}

      {/* 教科フィルター */}
      <div className="flex flex-wrap gap-2 mb-6">
        {ALL_SUBJECTS.map(subject => (
          <button
            key={subject.id}
            onClick={() => setActiveSubject(subject.id)}
            className={
              `px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200
              ${activeSubject === subject.id
                ? 'bg-purple-500 text-white'
                : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
              }`
            }
            aria-label={`${subject.name}を選択`}
          >
            {subject.name}
          </button>
        ))}
      </div>

      {/* コンテンツリスト */}
      <div className="space-y-4">
        {filteredContents.map((content) => (
          <div key={content.id} className="p-4 bg-pink-50 rounded shadow">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-semibold text-purple-800">{content.title}</h2>
                <p className="text-sm text-gray-600">教科: {getSubjectName(content.subject)}</p>
                <p className="text-sm text-gray-600">出題順序: {content.randomizeQuizzes ? 'ランダム' : '固定'}</p>
                {content.freeNavigation ? (
                  content.timeLimitPerContent > 0 && (
                    <p className="text-sm text-gray-600">全体制限時間: {content.timeLimitPerContent}秒</p>
                  )
                ) : (
                  content.timeLimitPerQuestion > 0 && (
                    <p className="text-sm text-gray-600">1問あたりの制限時間: {content.timeLimitPerQuestion}秒</p>
                  )
                )}
                <p className="text-sm text-gray-600">問題移動: {content.freeNavigation ? '自由' : '一方向'}</p>
                <p className="text-sm text-gray-600">解答表示モード: {content.answerRevealMode === 'immediate' ? '即時表示' : '最後まとめ表示'}</p>
                <p className="text-sm text-gray-600">
                  回答方式: {content.quickAnswerMode === 'quick' ? 'クイック回答' : '標準回答'}
                  {content.quickAnswerMode === 'quick' && ' (四択・正誤問題)'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setExpandedContents((prev) => ({
                    ...prev,
                    [content.id]: !prev[content.id],
                  }))}
                  className="text-purple-700 hover:text-purple-900 focus:outline-none"
                  aria-label={expandedContents[content.id] ? 'クイズ一覧を非表示にする' : 'クイズ一覧を表示する'}
                >
                  {expandedContents[content.id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>

                <button
                  onClick={() => {
                    startEditContent(content);
                    setContentModalOpen(true);
                  }}
                  className="text-blue-500 hover:text-blue-700"
                  aria-label={`コンテンツ${content.id}を編集`}
                >
                  <Edit2 size={20} />
                </button>

                <button
                  onClick={() => handleDeleteContentWithConfirm(content.id)}
                  className="text-red-500 hover:text-red-700"
                  aria-label={`コンテンツ${content.id}を削除`}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {expandedContents[content.id] && (
              <>
                <div className="space-y-2 mb-4">
                  {content.quizzes.length > 0 ? (
                    content.quizzes.map((quiz) => (
                      <div key={quiz.id} className="p-2 bg-white rounded shadow flex justify-between items-center">
                        <div>
                          <h3 className="text-md font-medium text-purple-700">
                            {quiz.type === 'multiple-choice' ? quiz.question
                              : quiz.type === 'sentence-rearrangement' ? quiz.englishSentence
                                : quiz.type === 'true-false' ? quiz.question
                                  : quiz.type === 'fill-in-the-blank' ? quiz.question
                                    : quiz.type === 'matching' ? quiz.question
                                      : ''}
                          </h3>
                          <p className="text-sm text-gray-600">教科: {getSubjectName(quiz.subject)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditQuiz(content.id, quiz)}
                            className="text-blue-500 hover:text-blue-700"
                            aria-label={`クイズ${quiz.id}を編集`}
                          >
                            <Edit2 size={20} />
                          </button>
                          <button
                            onClick={() => handleDeleteQuiz(content.id, quiz.id)}
                            className="text-red-500 hover:text-red-700"
                            aria-label={`クイズ${quiz.id}を削除`}
                          >
                            <X size={20} />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-600">このコンテンツにはクイズが含まれていません。</p>
                  )}
                </div>

                <div className="mt-4">
                  <button
                    onClick={() => handleAddQuiz(content.id, content.subject)}
                    className="w-full py-2 bg-teal-100 text-teal-700 rounded hover:bg-teal-200 flex items-center justify-center gap-2"
                    aria-label="新しいクイズを追加"
                  >
                    <PlusCircle size={20} />
                    新しいクイズを追加
                  </button>
                </div>
              </>
            )}
          </div>
        ))}

        {/* 新しいコンテンツを追加ボタン */}
        <div className="mt-6">
          <button
            onClick={() => {
              resetForm();
              setContentModalOpen(true);
            }}
            className="w-full py-4 bg-pink-100 text-pink-700 rounded-lg hover:bg-pink-200 flex items-center justify-center gap-2"
          >
            <PlusCircle size={20} />
            新しいコンテンツを追加
          </button>
        </div>
      </div>

      {/* モーダル */}
      <ContentEditModal 
        isOpen={contentModalOpen}
        onClose={() => {
          setContentModalOpen(false);
          resetForm();
        }}
        mode={editingContentId ? 'edit' : 'add'}
        newContent={newContent}
        setNewContent={setNewContent}
        handleAddContent={handleAddContentWithModal}
        handleUpdateContent={handleUpdateContentWithModal}
        formErrors={formErrors}
      />

      <QuizEditModal 
        isOpen={quizModalOpen}
        onClose={() => {
          setQuizModalOpen(false);
          setEditingQuiz(null);
          setAddingQuiz(null);
          setFormErrors({});
        }}
        mode={editingQuiz ? 'edit' : 'add'}
        quiz={editingQuiz ? editingQuiz.quiz : addingQuiz ? addingQuiz.quiz : {}}
        contentSubject={filteredContents.find(c => c.id === (editingQuiz?.contentId || addingQuiz?.contentId))?.subject}
        handleSubmitQuiz={handleSubmitQuiz}
        formErrors={formErrors}
        currentUser={currentUser}
      />
          
      {/* 確認ダイアログ */}
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
        />
      )}
    </div>
  );
};

QuizManagement.propTypes = {
  currentUser: PropTypes.shape({
    role: PropTypes.string.isRequired,
    isMaster: PropTypes.bool,
  }).isRequired,
};

export default QuizManagement;
